import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [trialActive, setTrialActive] = useState(false);
  const [trialDaysLeft, setTrialDaysLeft] = useState(0);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const getToken = () => localStorage.getItem('session_token');
  
  const saveToken = (token) => {
    if (token) {
      localStorage.setItem('session_token', token);
    } else {
      localStorage.removeItem('session_token');
    }
  };

  const createAuthHeaders = () => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const checkAuth = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setIsAuthenticated(false);
      setIsPro(false);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      setIsAuthenticated(true);
      setIsPro(response.data.has_pro_access ?? (response.data.is_pro || response.data.role === 'admin'));
      setTrialActive(response.data.trial_active || false);
      setTrialDaysLeft(response.data.trial_days_left || 0);
    } catch (error) {
      console.error('Auth check failed:', error?.response?.status);
      saveToken(null);
      setUser(null);
      setIsAuthenticated(false);
      setIsPro(false);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    // If returning from OAuth callback, don't check auth yet
    // AuthCallback will handle the session exchange and then redirect
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    const redirectUrl = window.location.origin + '/forum';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: createAuthHeaders()
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      saveToken(null);
      setUser(null);
      setIsAuthenticated(false);
      setIsPro(false);
      window.location.href = '/';
    }
  };

  const refreshAuth = () => checkAuth();

  const value = {
    user,
    isAuthenticated,
    isPro,
    trialActive,
    trialDaysLeft,
    loading,
    login,
    logout,
    setUser,
    setIsAuthenticated,
    setIsPro,
    saveToken,
    getToken,
    createAuthHeaders,
    refreshAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
